import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Send, X, Loader2, Minus, Paperclip, FileText, Image, Trash2, CheckCircle2, FolderPlus, Calendar, ListTodo, AlertCircle, MessageSquare, History, Maximize2, Minimize2, Save, Cloud, CloudOff } from 'lucide-react';
import { useUserEmail, useUserId, useBaseUserId } from '../context/UserAuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * CounterfindAIChat
 * ------------------------------------------------------------------
 * Floating AI chat launcher that appears on EVERY page. Supports:
 * - Text chat with AI assistant
 * - File uploads (images, PDFs, docs)
 * - Task execution (add to documents, create meetings, etc.)
 * - Conversation persistence across refresh/redeploy
 */
const CounterfindAIChat = () => {
  const userEmail = useUserEmail();
  const userId = useUserId();
  const baseUserId = useBaseUserId();
  const isCounterfind = (userEmail || '').toLowerCase() === 'counterfind@operatr.com';

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [savedConversations, setSavedConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const fileInputRef = useRef(null);
  
  const getDefaultMessage = () => ({
    role: 'assistant',
    content: (userEmail || '').toLowerCase() === 'counterfind@operatr.com'
      ? "Hi — I'm your CounterFind assistant. Ask me about counterfeit cases, takedowns, queue triage, or any brand-protection workflow."
      : "Hi — I'm your OPERATR assistant. I can help you:\n\n• Upload files to Documents\n• Schedule meetings & tasks\n• Search your data\n• Answer questions\n\nTry: \"Add this file to my documents\" or \"What are my tasks today?\"",
    timestamp: Date.now()
  });
  
  const [messages, setMessages] = useState(() => [getDefaultMessage()]);
  
  // Stable session id for this conversation
  const sessionIdRef = useRef(
    `operatr-chat-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`,
  );
  const scrollRef = useRef(null);

  // Load saved conversations on mount
  useEffect(() => {
    if (!userId || conversationsLoaded) return;
    
    const loadConversations = async () => {
      try {
        const res = await fetch(`${API}/api/ai-assistant/conversations/${encodeURIComponent(userId)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.conversations?.length > 0) {
            setSavedConversations(data.conversations);
            // Load the most recent conversation if it has messages
            const mostRecent = data.conversations[0];
            if (mostRecent.id) {
              // Fetch full conversation with messages
              const fullRes = await fetch(`${API}/api/ai-assistant/conversations/${encodeURIComponent(userId)}/${mostRecent.id}`);
              if (fullRes.ok) {
                const fullData = await fullRes.json();
                if (fullData.conversation?.messages?.length > 0) {
                  setMessages(fullData.conversation.messages);
                  sessionIdRef.current = fullData.conversation.session_id || sessionIdRef.current;
                }
              }
            }
          }
        }
      } catch (err) {
        console.warn('Could not load conversations:', err);
      } finally {
        setConversationsLoaded(true);
      }
    };
    
    loadConversations();
  }, [userId, conversationsLoaded]);

  // Save conversation to backend whenever messages change
  const saveConversation = useCallback(async (msgs, manual = false) => {
    if (!userId || msgs.length <= 1) return; // Don't save just the welcome message
    
    setSaveStatus('saving');
    try {
      const res = await fetch(`${API}/api/ai-assistant/conversations/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          session_id: sessionIdRef.current,
          title: msgs.find(m => m.role === 'user')?.content?.slice(0, 50) || 'Conversation',
          messages: msgs,
          updated_at: Date.now()
        })
      });
      if (res.ok) {
        setSaveStatus('saved');
        if (manual) {
          // Refresh saved conversations list
          const listRes = await fetch(`${API}/api/ai-assistant/conversations/${encodeURIComponent(userId)}`);
          if (listRes.ok) {
            const data = await listRes.json();
            setSavedConversations(data.conversations || []);
          }
        }
        // Reset status after 2 seconds
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch (err) {
      console.warn('Could not save conversation:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [userId]);

  // Auto-save when messages change (debounced)
  useEffect(() => {
    if (messages.length <= 1) return;
    const timer = setTimeout(() => saveConversation(messages), 1000);
    return () => clearTimeout(timer);
  }, [messages, saveConversation]);

  // Start a new conversation
  const startNewConversation = () => {
    sessionIdRef.current = `operatr-chat-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
    setMessages([getDefaultMessage()]);
    setShowHistory(false);
  };

  // Load a saved conversation - fetch full messages from backend
  const loadConversation = async (conv) => {
    try {
      // Fetch full conversation with all messages
      const res = await fetch(`${API}/api/ai-assistant/conversations/${encodeURIComponent(userId)}/${conv.id}`);
      if (res.ok) {
        const data = await res.json();
        if (data.conversation?.messages?.length > 0) {
          setMessages(data.conversation.messages);
          sessionIdRef.current = data.conversation.session_id || conv.session_id || sessionIdRef.current;
        } else if (conv.messages?.length > 0) {
          // Fallback to embedded messages if available
          setMessages(conv.messages);
          sessionIdRef.current = conv.session_id || sessionIdRef.current;
        } else {
          setMessages([getDefaultMessage()]);
        }
      } else {
        // Fallback to embedded messages
        if (conv.messages?.length > 0) {
          setMessages(conv.messages);
          sessionIdRef.current = conv.session_id || sessionIdRef.current;
        }
      }
    } catch (err) {
      console.warn('Could not load conversation:', err);
      // Fallback to embedded messages
      if (conv.messages?.length > 0) {
        setMessages(conv.messages);
        sessionIdRef.current = conv.session_id || sessionIdRef.current;
      }
    }
    setShowHistory(false);
  };

  // Delete a saved conversation
  const deleteConversation = async (convId, e) => {
    e.stopPropagation(); // Prevent triggering loadConversation
    
    if (!confirm('Delete this conversation?')) return;
    
    try {
      const res = await fetch(`${API}/api/ai-assistant/conversations/${encodeURIComponent(userId)}/${convId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        // Remove from local state
        setSavedConversations(prev => prev.filter(c => c.id !== convId));
        
        // If we deleted the currently active conversation, start a new one
        if (sessionIdRef.current === convId) {
          startNewConversation();
        }
      }
    } catch (err) {
      console.warn('Could not delete conversation:', err);
    }
  };

  // Fire the counterfind extras seed once per session so Documents, Clients,
  // Employees, Lead Generation, Finances, Meetings, and Calendar all show
  // mockup data for this tenant. Idempotent on the backend — safe to call
  // repeatedly. Version is bumped whenever we ship new mock collections so
  // existing tenants get the upgrade automatically on next visit.
  useEffect(() => {
    if (!isCounterfind || !baseUserId) return;
    const SEED_VERSION = 4;  // bump when seed data changes (real brand logos)
    const flagKey = `cf_extras_seeded:${baseUserId}:v${SEED_VERSION}`;
    if (sessionStorage.getItem(flagKey)) return;
    // If THIS exact version flag isn't set, force-reseed. That covers both the
    // first-ever visit AND any prior version (v1, v2, v3, …) — we don't want
    // to leave stale mock data behind when the seed shape changes.
    fetch(`${window.location.origin}/api/counterfind/extras/seed?base_user_id=${encodeURIComponent(baseUserId)}&force=true`, {
      method: 'POST',
    })
      .then(() => {
        sessionStorage.setItem(flagKey, '1');
        // Hint the user / dev that mock data was refreshed — pages mounted
        // BEFORE the seed completes won't reflect the new rows until reload.
        try {
          window.dispatchEvent(new CustomEvent('cf-extras-reseeded'));
        } catch { /* noop */ }
      })
      .catch((err) => console.warn('Counterfind extras seed failed:', err));
  }, [isCounterfind, baseUserId]);

  // Auto-scroll to newest message whenever messages change.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Floating AI chat is now visible to every signed-in user. The
  // counterfind extras seed above stays counterfind-only.
  if (!userEmail) return null;

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => {
      const ext = f.name.toLowerCase().split('.').pop();
      const validExts = ['pdf', 'doc', 'docx', 'txt', 'csv', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
      return validExts.includes(ext) && f.size <= 25 * 1024 * 1024; // 25MB limit
    });
    setAttachedFiles(prev => [...prev, ...validFiles]);
    e.target.value = ''; // Reset input
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      return <Image size={12} className="text-purple-400" />;
    }
    return <FileText size={12} className="text-blue-400" />;
  };

  // Upload files to documents page
  const uploadToDocuments = async (files, folderName = 'AI Assistant Uploads') => {
    const results = [];
    console.log('🤖 AI Assistant: Starting upload of', files.length, 'file(s) to', folderName);
    
    // First, try to find or create folder
    let folderId = null;
    try {
      // Check if folder exists
      const foldersRes = await fetch(`${API}/api/documents-storage/folders?user_id=${encodeURIComponent(userId)}`);
      if (foldersRes.ok) {
        const foldersData = await foldersRes.json();
        const existingFolder = foldersData.folders?.find(f => f.name.toLowerCase() === folderName.toLowerCase());
        if (existingFolder) {
          folderId = existingFolder.id;
          console.log('🤖 Found existing folder:', folderId);
        } else {
          // Create new folder
          const createRes = await fetch(`${API}/api/documents-storage/folders?user_id=${encodeURIComponent(userId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: folderName, color: 'bg-purple-500' })
          });
          if (createRes.ok) {
            const newFolder = await createRes.json();
            folderId = newFolder.id;
            console.log('🤖 Created new folder:', folderId);
          }
        }
      }
    } catch (err) {
      console.warn('🤖 Could not create/find folder:', err);
    }
    
    // Upload each file
    for (const file of files) {
      try {
        console.log('🤖 Uploading file:', file.name);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name);
        formData.append('user_id', userId);
        formData.append('category', 'AI Upload');
        if (folderId) {
          formData.append('folder_id', folderId);
        }
        
        const res = await fetch(`${API}/api/documents-storage/upload`, {
          method: 'POST',
          body: formData,
        });
        
        if (res.ok) {
          const data = await res.json();
          console.log('🤖 Upload success:', file.name, data);
          results.push({ success: true, filename: file.name, id: data.id });
        } else {
          const errText = await res.text();
          console.error('🤖 Upload failed:', file.name, errText);
          results.push({ success: false, filename: file.name, error: errText });
        }
      } catch (err) {
        console.error('🤖 Upload error:', file.name, err);
        results.push({ success: false, filename: file.name, error: err.message });
      }
    }
    return results;
  };

  // Execute confirmed action
  const executeAction = async (action) => {
    setActionLoading(true);
    try {
      if (action.type === 'upload_to_documents') {
        const results = await uploadToDocuments(action.files, action.folder);
        const successCount = results.filter(r => r.success).length;
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✅ Done! Uploaded ${successCount}/${results.length} file(s) to Documents${action.folder ? ` in "${action.folder}"` : ''}.`,
          actionResult: { type: 'upload', results }
        }]);
      } else if (action.type === 'create_meeting') {
        // Meeting creation is handled by the AI response parser
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `✅ Meeting "${action.title}" scheduled for ${action.date}${action.time ? ` at ${action.time}` : ''}.`,
          actionResult: { type: 'meeting', ...action }
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `❌ Sorry, I couldn't complete that action: ${err.message}`,
      }]);
    } finally {
      setActionLoading(false);
      setPendingAction(null);
    }
  };

  const send = async () => {
    const text = input.trim();
    if ((!text && attachedFiles.length === 0) || loading) return;
    
    const hasFiles = attachedFiles.length > 0;
    const fileNames = attachedFiles.map(f => f.name);
    
    setInput('');
    
    // Build user message content
    let userContent = text;
    if (hasFiles) {
      userContent = text || `Upload ${attachedFiles.length} file(s) to documents`;
      if (fileNames.length > 0) {
        userContent += `\n\n📎 Attached: ${fileNames.join(', ')}`;
      }
    }
    
    const userMessage = { role: 'user', content: userContent, timestamp: Date.now() };
    setMessages((prev) => [...prev, userMessage]);
    
    // Check for direct upload intent - more flexible matching
    const uploadIntent = hasFiles && (
      /add|upload|save|put|store|send/i.test(text) || 
      /document|file|folder/i.test(text) ||
      text === '' // If they just attach files and hit send, assume upload intent
    );
    
    if (hasFiles && uploadIntent) {
      // Extract folder name if mentioned
      const folderMatch = text.match(/(?:to|in|into)\s+(?:the\s+)?["']?([^"'\n,]+?)["']?\s*(?:folder|page|section)?(?:\s|$)/i);
      const folder = folderMatch ? folderMatch[1].trim().replace(/^my\s+/i, '') : 'AI Assistant Uploads';
      
      // Skip confirmation - just upload directly for better UX
      setLoading(true);
      const filesToUpload = [...attachedFiles];
      setAttachedFiles([]);
      
      try {
        const results = await uploadToDocuments(filesToUpload, folder);
        const successCount = results.filter(r => r.success).length;
        const failCount = results.length - successCount;
        
        let responseContent = '';
        if (successCount === results.length) {
          responseContent = `✅ Done! I've uploaded ${successCount} file${successCount !== 1 ? 's' : ''} to your Documents page${folder !== 'AI Assistant Uploads' ? ` in the "${folder}" folder` : ''}.\n\nYou can view them on the Documents page.`;
        } else if (successCount > 0) {
          responseContent = `⚠️ Uploaded ${successCount}/${results.length} file(s). ${failCount} file(s) failed to upload.`;
        } else {
          responseContent = `❌ Sorry, I couldn't upload the files. Please try again or upload directly from the Documents page.`;
        }
        
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: responseContent,
          timestamp: Date.now(),
          actionResult: { type: 'upload', results }
        }]);
      } catch (err) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `❌ Upload failed: ${err.message}`,
          timestamp: Date.now()
        }]);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Clear files after adding to message
    const filesToSend = [...attachedFiles];
    setAttachedFiles([]);
    
    setLoading(true);
    try {
      // Build message with file context
      let messageText = text;
      if (filesToSend.length > 0) {
        messageText += `\n\n[User has attached ${filesToSend.length} file(s): ${fileNames.join(', ')}. They may want to upload these to Documents, use them for transcription, or ask about them.]`;
      }
      
      const res = await fetch(`${API}/api/ai-assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          message: messageText,
          model: 'gemini',
          user_id: userId,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      
      // Check if AI detected an action intent
      let responseContent = data.response || '(empty response)';
      
      // If user had files and AI suggests uploading
      if (filesToSend.length > 0 && !uploadIntent) {
        responseContent += `\n\n💡 *You have files attached. Say "add to documents" to upload them, or ask me to help with something else.*`;
      }
      
      setMessages((prev) => [
        ...prev,
        { 
          role: 'assistant', 
          content: responseContent,
          actionResult: data.action_result
        },
      ]);
      
      // If action was executed, show feedback
      if (data.action_result?.success) {
        // Action was auto-executed by backend
      }
      
    } catch (err) {
      console.error('OPERATR AI chat error:', err);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry — I hit a snag. Please try again in a moment.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open OPERATR AI Chat"
          data-testid="counterfind-chat-launcher"
          className="fixed bottom-5 right-5 z-[9999] w-14 h-14 rounded-full bg-black border border-gray-700 shadow-[0_8px_28px_rgba(0,0,0,0.6)] hover:scale-105 hover:border-gray-400 transition-all flex items-center justify-center overflow-hidden"
        >
          <img
            src="/branding/counterfind-chat-icon.png"
            alt="OPERATR AI"
            className="w-12 h-12 object-contain"
          />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          data-testid="counterfind-chat-window"
          className={`fixed z-[9999] bg-black border border-gray-600 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden transition-all duration-300 ease-in-out ${
            expanded 
              ? 'bottom-4 right-4 w-[700px] max-w-[95vw] h-[85vh] max-h-[800px]' 
              : 'bottom-5 right-5 w-[360px] max-w-[92vw] h-[520px] max-h-[80vh]'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-gray-800 bg-black shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <img
                src="/branding/counterfind-chat-icon.png"
                alt=""
                className="w-7 h-7 object-contain rounded-full bg-black"
              />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white truncate">OPERATR AI</div>
                <div className="text-[10px] text-gray-500 truncate">{expanded ? 'Expanded view · Click arrows to minimize' : 'Your personal assistant'}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Save Status Indicator */}
              {messages.length > 1 && (
                <button
                  onClick={() => saveConversation(messages, true)}
                  disabled={saveStatus === 'saving'}
                  aria-label="Save conversation"
                  title={saveStatus === 'saved' ? 'Conversation saved!' : saveStatus === 'error' ? 'Save failed - click to retry' : 'Save conversation'}
                  className={`p-1.5 hover:bg-gray-800 rounded transition-colors ${
                    saveStatus === 'saved' ? 'text-emerald-400' : 
                    saveStatus === 'error' ? 'text-red-400' : 
                    saveStatus === 'saving' ? 'text-yellow-400' : ''
                  }`}
                >
                  {saveStatus === 'saving' ? (
                    <Loader2 size={14} className="animate-spin text-yellow-400" />
                  ) : saveStatus === 'saved' ? (
                    <Cloud size={14} className="text-emerald-400" />
                  ) : saveStatus === 'error' ? (
                    <CloudOff size={14} className="text-red-400" />
                  ) : (
                    <Save size={14} className="text-gray-400" />
                  )}
                </button>
              )}
              <button
                onClick={startNewConversation}
                aria-label="New conversation"
                title="Start new conversation"
                className="p-1.5 hover:bg-gray-800 rounded transition-colors"
              >
                <MessageSquare size={14} className="text-gray-400" />
              </button>
              <button
                onClick={() => setShowHistory(!showHistory)}
                aria-label="Conversation history"
                title="View history"
                className={`p-1.5 hover:bg-gray-800 rounded transition-colors ${showHistory ? 'bg-gray-800' : ''}`}
              >
                <History size={14} className="text-gray-400" />
              </button>
              <button
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? "Minimize" : "Expand"}
                title={expanded ? "Minimize chat" : "Expand chat"}
                data-testid="counterfind-chat-expand"
                className="p-1.5 hover:bg-gray-800 rounded transition-colors"
              >
                {expanded ? <Minimize2 size={14} className="text-gray-400" /> : <Maximize2 size={14} className="text-gray-400" />}
              </button>
              <button
                onClick={() => setOpen(false)}
                aria-label="Minimise chat"
                data-testid="counterfind-chat-minimise"
                className="p-1.5 hover:bg-gray-800 rounded transition-colors"
              >
                <Minus size={14} className="text-gray-400" />
              </button>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                data-testid="counterfind-chat-close"
                className="p-1.5 hover:bg-gray-800 rounded transition-colors"
              >
                <X size={14} className="text-gray-400" />
              </button>
            </div>
          </div>

          {/* History Panel */}
          {showHistory && (
            <div className="absolute inset-0 top-[52px] bg-black z-10 flex flex-col">
              <div className="p-3 border-b border-gray-800">
                <h3 className="text-sm font-medium text-white">Conversation History</h3>
                <p className="text-xs text-gray-500">Your saved conversations · Click to load</p>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {savedConversations.length === 0 ? (
                  <div className="text-center py-8">
                    <History size={32} className="text-gray-700 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">No saved conversations yet</p>
                    <p className="text-xs text-gray-600 mt-1">Your chats will appear here</p>
                  </div>
                ) : (
                  savedConversations.map((conv, idx) => (
                    <div
                      key={conv.id || idx}
                      className="group flex items-center gap-2 p-2 rounded-lg hover:bg-gray-900 transition-colors cursor-pointer"
                      onClick={() => loadConversation(conv)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{conv.title || 'Conversation'}</div>
                        <div className="text-xs text-gray-500">
                          {conv.message_count || 0} messages · {conv.updated_at ? new Date(conv.updated_at).toLocaleDateString() : 'Recent'}
                        </div>
                      </div>
                      <button
                        onClick={(e) => deleteConversation(conv.id, e)}
                        className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded transition-all"
                        title="Delete conversation"
                      >
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  ))
                )}
              </div>
              <div className="p-2 border-t border-gray-800">
                <button
                  onClick={() => { startNewConversation(); setShowHistory(false); }}
                  className="w-full py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm rounded-lg transition-colors"
                >
                  + New Conversation
                </button>
              </div>
            </div>
          )}

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto custom-scrollbar px-3 py-3 space-y-2 bg-black"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] text-sm rounded-2xl px-3 py-2 whitespace-pre-wrap leading-snug break-words ${
                    m.role === 'user'
                      ? 'bg-gradient-to-br from-cyan-600 to-cyan-700 text-white'
                      : 'bg-gray-900 text-gray-100 border border-gray-800'
                  }`}
                  style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                  data-testid={`counterfind-chat-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-900 border border-gray-800 rounded-2xl px-3 py-2 text-sm text-gray-400 flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-gray-800 p-2.5 bg-black">
            {/* Attached Files Preview */}
            {attachedFiles.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {attachedFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 bg-gray-900 border border-gray-700 rounded-lg px-2 py-1 text-xs">
                    {getFileIcon(file.name)}
                    <span className="text-gray-300 truncate max-w-[100px]">{file.name}</span>
                    <button onClick={() => removeFile(idx)} className="text-gray-500 hover:text-red-400">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pending Action Confirmation */}
            {pendingAction && (
              <div className="mb-2 bg-blue-500/10 border border-blue-500/30 rounded-lg p-2">
                <p className="text-xs text-blue-300 mb-2">{pendingAction.message}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => executeAction(pendingAction)}
                    disabled={actionLoading}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-lg disabled:opacity-50"
                  >
                    {actionLoading ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />}
                    Yes, proceed
                  </button>
                  <button
                    onClick={() => setPendingAction(null)}
                    disabled={actionLoading}
                    className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex items-end gap-2">
              {/* File Upload Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="w-9 h-9 rounded-xl bg-gray-900 hover:bg-gray-800 border border-gray-700 text-gray-400 hover:text-white flex items-center justify-center transition-colors shrink-0"
                title="Attach files (images, PDFs, documents)"
              >
                <Paperclip size={14} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder={attachedFiles.length > 0 ? "Describe what to do with these files..." : "Ask the OPERATR AI…"}
                data-testid="counterfind-chat-input"
                className="flex-1 resize-none bg-gray-950 border border-gray-800 focus:border-gray-500 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 outline-none max-h-28"
              />
              <button
                onClick={send}
                disabled={loading || (!input.trim() && attachedFiles.length === 0)}
                data-testid="counterfind-chat-send"
                className="w-9 h-9 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-800 disabled:text-gray-500 text-white flex items-center justify-center transition-colors shrink-0"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
            
            {/* Quick Actions */}
            {!loading && messages.length <= 2 && attachedFiles.length === 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                <button
                  onClick={() => setInput('What meetings do I have today?')}
                  className="text-[10px] px-2 py-1 bg-gray-900 hover:bg-gray-800 border border-gray-700 rounded-full text-gray-400 hover:text-white transition-colors"
                >
                  📅 Today&apos;s meetings
                </button>
                <button
                  onClick={() => setInput('What are my open tasks?')}
                  className="text-[10px] px-2 py-1 bg-gray-900 hover:bg-gray-800 border border-gray-700 rounded-full text-gray-400 hover:text-white transition-colors"
                >
                  ✅ My tasks
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-[10px] px-2 py-1 bg-gray-900 hover:bg-gray-800 border border-gray-700 rounded-full text-gray-400 hover:text-white transition-colors"
                >
                  📎 Upload files
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CounterfindAIChat;
